/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useContext, useEffect, useState } from 'react';
import { ContextStatus, ENumberProducts } from '@leedpulse-monorepo/enums';
import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import getNumbers from '../../../domain/use_cases/numbers/getNumbers';
import searchNumbers from '../../../domain/use_cases/numbers/searchNumbers';
import getNumberById from '../../../domain/use_cases/numbers/getNumberById';
import getNumbersFromSenders from '../../../domain/use_cases/numbers/getNumbersFromSenders';
import NumberOptions from '../../../domain/types/NumberOptions';
import { INumber } from '@leedpulse-monorepo/interfaces';
import AuthenticationContext from '../auth/AuthenticationContext';

const NumbersContext = createContext({
  searchQuery: '',
  count: 0,
  assignedCount: 0,
  availableCount: 0,
  items: [] as INumber[],
  selected: null as INumber | null,
  lastVisible: {} as QueryDocumentSnapshot<DocumentData, DocumentData>,
  searchResults: [] as INumber[],
  noSearchResults: false,
  fetch: (options?: NumberOptions) => {},
  fetchNext: (options?: NumberOptions) => {},
  fetchAndSelectById: (_: string) => {},
  fetchFromSenders: (_: string[]) => {},
  select: (_: INumber) => {},
  search: (_: string, options?: NumberOptions) => {},
  resetSearch: () => {},
  reset: () => {},
  status: ContextStatus.IDLE,
  selectionStatus: ContextStatus.IDLE,
  sendersStatus: ContextStatus.IDLE,
  productConstraint: undefined as ENumberProducts | undefined,
  onProductConstraintChange: (_: ENumberProducts | undefined) => {},
});

export const NUMBERS_FETCH_LIMIT = 10;

export const NumbersProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const authContext = useContext(AuthenticationContext);
  const [count, setCount] = useState(0);
  const [assignedCount, setAssignedCount] = useState(0);
  const [availableCount, setAvailableCount] = useState(0);
  const [items, setItems] = useState([] as INumber[]);
  const [selected, setSelected] = useState(null as INumber | null);
  const [lastVisible, setLastVisible] = useState(
    {} as QueryDocumentSnapshot<DocumentData, DocumentData>
  );
  const [searchResults, setSearchResults] = useState([] as INumber[]);
  const [searchQuery, setSearchQuery] = useState('');
  const [noSearchResults, setNoSearchResults] = useState(false);
  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [selectionStatus, setSelectionStatus] = useState(ContextStatus.IDLE);
  const [sendersStatus, setSendersStatus] = useState(ContextStatus.IDLE);
  const [productConstraint, setProductConstraint] = useState<
    ENumberProducts | undefined
  >(undefined);
  const [isFirstRender, setIsFirstRender] = useState(true);

  useEffect(() => {
    if (isFirstRender) {
      setIsFirstRender(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isFirstRender) return;
    if (searchQuery) {
      search(searchQuery, { product: productConstraint });
    } else {
      fetch({ product: productConstraint });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productConstraint]);

  const fetch = async (options?: NumberOptions) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getNumbers(
        NUMBERS_FETCH_LIMIT,
        undefined,
        options
      );
      setItems(useCaseResponse.numbers);
      setLastVisible(useCaseResponse.lastVisible);
      setCount(useCaseResponse.totalCollectionCount);
      if (useCaseResponse.availabilityCounts) {
        setAssignedCount(useCaseResponse.availabilityCounts.assigned);
        setAvailableCount(useCaseResponse.availabilityCounts.available);
      }
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchNext = async (options?: NumberOptions) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getNumbers(
        NUMBERS_FETCH_LIMIT,
        lastVisible,
        options
      );
      setItems([...items, ...useCaseResponse.numbers]);
      setLastVisible(useCaseResponse.lastVisible);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchAndSelectById = async (id: string) => {
    setSelectionStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getNumberById(id);
      if (useCaseResponse) {
        setSelected(useCaseResponse);
        setSelectionStatus(ContextStatus.SUCCESS);
      } else {
        setSelectionStatus(ContextStatus.ERROR);
      }
    } catch (e) {
      setSelectionStatus(ContextStatus.ERROR);
    }
  };

  const fetchFromSenders = async (senders: string[]) => {
    setSendersStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getNumbersFromSenders(senders);
      if (useCaseResponse) {
        setItems(useCaseResponse.numbers);
        setSendersStatus(ContextStatus.SUCCESS);
      } else {
        setSendersStatus(ContextStatus.ERROR);
      }
    } catch (e) {
      setSendersStatus(ContextStatus.ERROR);
    }
  };

  const select = (number: INumber) => {
    setSelected(number);
    setSelectionStatus(ContextStatus.SUCCESS);
  };

  const search = async (searchQuery: string, options?: NumberOptions) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await searchNumbers(
        authContext.authentication,
        searchQuery,
        0,
        20,
        options
      );
      const results: INumber[] = useCaseResponse.hits.map(
        ({ objectID, ...hit }) => ({
          ...hit,
          sender: hit.sender || objectID,
        })
      );
      const availableResultsCount = results.filter((number) => number.available)
        .length;

      setSearchResults(results);
      setNoSearchResults(results.length === 0);
      setSearchQuery(searchQuery);
      setCount(useCaseResponse.nbHits);
      setAvailableCount(availableResultsCount);
      setAssignedCount(results.length - availableResultsCount);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const onProductConstraintChange = (product: ENumberProducts | undefined) => {
    setProductConstraint(product);
  };

  const resetSearch = () => {
    setSearchResults([]);
    setNoSearchResults(false);
    setSearchQuery('');
  };

  const reset = () => {
    setSearchQuery('');
    setItems([]);
    setCount(0);
    setSelected(null);
    setLastVisible({} as QueryDocumentSnapshot<DocumentData, DocumentData>);
    setSearchResults([]);
    setNoSearchResults(false);
    setStatus(ContextStatus.IDLE);
    setSendersStatus(ContextStatus.IDLE);
  };

  return (
    <NumbersContext.Provider
      value={{
        searchQuery,
        count,
        assignedCount,
        availableCount,
        items,
        selected,
        lastVisible,
        searchResults,
        noSearchResults,
        fetch,
        fetchNext,
        fetchAndSelectById,
        fetchFromSenders,
        select,
        search,
        resetSearch,
        reset,
        status,
        selectionStatus,
        sendersStatus,
        productConstraint,
        onProductConstraintChange,
      }}
    >
      {children}
    </NumbersContext.Provider>
  );
};

export default NumbersContext;
